import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { PageLoader } from "../admin/common/loading";
import { canReloadForStaleChunk, isStaleChunkError, reloadOnceForStaleChunk } from "../../utils/helpers/staleChunkReload";

interface ErrorFallbackProps {
  error: unknown;
  resetError: () => void;
}

const ErrorFallback = ({ error, resetError }: ErrorFallbackProps) => {
  const [reloading] = useState(() => isStaleChunkError(error) && canReloadForStaleChunk());

  useEffect(() => {
    if (reloading) {
      reloadOnceForStaleChunk();
    }
  }, [reloading]);

  if (reloading) return <PageLoader fullScreen />;

  const handleReload = () => {
    resetError();
    window.location.reload();
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-[#0b0a07] px-6">
      <div className="w-full max-w-md rounded-2xl border border-[#BBA473]/20 bg-[#141009]/85 px-8 py-10 text-center shadow-[0_8px_44px_rgba(0,0,0,0.5)]">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border border-[#BBA473]/30 bg-[#BBA473]/10">
          <AlertTriangle size={28} strokeWidth={1.5} className="text-[#BBA473]" />
        </div>

        <h2 className="text-xl font-bold text-[#F5F1E6]">Something went wrong</h2>
        <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-[#8b8270]">
          An unexpected error occurred. Reloading the page usually fixes it.
        </p>

        {import.meta.env.DEV && error instanceof Error && (
          <pre className="mt-4 max-h-40 overflow-auto rounded-lg bg-black/40 p-3 text-left text-[11px] leading-relaxed text-[#c9a875]">
            {error.message}
          </pre>
        )}

        <button
          onClick={handleReload}
          className="mt-7 inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-[#C6AF7C] to-[#A88F5F] px-5 py-2.5 text-sm font-semibold text-black transition-all hover:brightness-105 active:scale-[0.98] cursor-pointer"
        >
          Reload Page
        </button>
      </div>
    </div>
  );
};

export default ErrorFallback;
